<template>
  <div class="container">
    <paper-simple v-if="loading">
      <loading-page />
    </paper-simple>
    <div
      v-else
      class="monitoring-wrapper"
    >
      <div class="d-flex justify-content-between flex-wrap mb-2">
        <div class="d-flex justify-content-between">
          <b-dropdown
            id="dropdown-2"
            :text="`${selectedDataCenter.name} - ${selectedDataCenter.slug}`"
            variant="bg-white"
            class="dropdown-icon-wrapper"
          >
            <template #button-content>
              <span><b>{{ selectedDataCenter.name }}</b> - {{ selectedDataCenter.slug }}</span>
              <GjIcon
                class="icon-right"
                name="ArrowDown"
              />
            </template>
            <template v-for="dataCenter in healthcheckDataCenters">
              <b-dropdown-item
                :key="dataCenter.id"
                @click="changeDataCenter(dataCenter)"
              >
                <b>{{ dataCenter.name }}</b> - {{ dataCenter.slug }}
              </b-dropdown-item>
            </template>
          </b-dropdown>
        </div>
        <div class="justify-content-end mt-1 mt-md-0">
          <calendar-min-max-range
            @onUpdate="updateDateRange"
          />
        </div>
      </div>
      <div class="d-flex justify-content-between flex-wrap mb-2 main-details">
        <input-field
          label="Expected Response"
          label-position="top"
          class="px-1 col-12 col-sm-6 col-lg-3 details-input"
        >
          <span class="details-input-label expected-response">
            {{ healthcheck.expectedResponse || '-' }}:
            <span :class="`status-code-${responseStatusCodeRange}`">
              {{
                getText(
                  healthcheck.httpStatusCodes,
                  healthcheck.expectedResponse,
                ) || '-'
              }}
            </span>
          </span>
        </input-field>
        <input-field
          label="Interval"
          label-position="top"
          class="px-1 col-12 col-sm-6 col-lg-3 details-input"
        >
          <span class="details-input-label">
            {{ returnSchedule(healthcheck) }}
          </span>
        </input-field>
        <input-field
          label="Timeout"
          label-position="top"
          class="px-1 col-12 col-sm-6 col-lg-3 details-input"
        >
          <span v-b-tooltip.hover.bottom="`${healthcheck.timeout}ms`">
            <span class="details-input-label">
              {{ formatMilliseconds(healthcheck.timeout) }}
            </span>
          </span>
        </input-field>
        <input-field
          label="Retries"
          label-position="top"
          class="px-1 col-12 col-sm-6 col-lg-3 details-input"
        >
          <span class="details-input-label">
            {{ healthcheck.retries || '-' }}
          </span>
        </input-field>
      </div>
      <div class="d-flex justify-content-between flex-wrap mb-2">
        <div class="col-12 monitoring-chart-details mb-1 p-0">
          <div class="d-flex justify-content-between">
            <h5 class="heading">
              Response Time (ms)
            </h5>
            <div class="d-flex mb-1">
              <b-dropdown
                id="aggregation-type"
                :text="`${selectedAggregation.value}`"
                variant="bg-white"
                class="dropdown-icon-wrapper"
              >
                <template #button-content>
                  <span><b>{{ selectedAggregation.value }}</b></span>
                  <GjIcon
                    class="icon-right"
                    name="ArrowDown"
                  />
                </template>
                <template v-for="aggregation in aggregationTypes">
                  <b-dropdown-item
                    :key="aggregation.id"
                    @click="changeAggregationType(aggregation)"
                  >
                    {{ aggregation.value }}
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </div>
          </div>
          <res-time-area-chart />
        </div>
        <div class="col-12 monitoring-chart-details">
          <div class="d-flex justify-content-start">
            <h5 class="heading">
              Healthcheck Uptime
            </h5>
          </div>
          <status-chart />
        </div>
      </div>
      <div class="d-flex justify-content-between flex-wrap mb-2">
        <div
          v-if="!loadingCount"
          class="col-12 monitoring-chart-details"
        >
          <div class="d-flex flex-wrap mb-1 chart-wrapper">
            <statistic
              class="p-1 col-12 col-sm-6 col-lg-3"
              :title="healthcheckLogsCount ? healthcheckLogsCount.toLocaleString() : '0'"
              subtitle="Total Logs"
              icon="ArrowRight"
            />
            <statistic
              class="p-1 col-sm-6 col-lg-3"
              :title="healthcheckLogsCount ? `${medianResTime}ms` : 'N/A'"
              subtitle="Response Time(Median)"
              icon="ArrowRight"
            />
            <statistic
              class="p-1 col-sm-6 col-lg-3"
              :title="healthcheckLogsCount ? `${averageResTime}ms` : 'N/A'"
              subtitle="Response Time(Avg)"
              icon="ArrowRight"
            />
            <statistic
              class="p-1 col-sm-6 col-lg-3"
              :title="healthcheckLogsCount ? `${highestResTime}ms` : 'N/A'"
              subtitle="Response Time(Max)"
              icon="ArrowRight"
            />
          </div>
        </div>
        <div
          v-else
          class="col-12 monitoring-chart-details"
        >
          <div class="d-flex justify-content-center flex-wrap mb-1 chart-wrapper">
            <loading-page class="loading-wrapper" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ResTimeAreaChart from '../../healthcheck-logs/Monitoring/ResTimeAreaChart.vue'
import StatusChart from '../../healthcheck-logs/Monitoring/StatusChart.vue'
import Statistic from '@/components/statistics/Statistic.vue'
import CalendarMinMaxRange from '@/components/calendar/CalendarMinMaxRange.vue'

export default {
  components: {
    ResTimeAreaChart,
    StatusChart,
    Statistic,
    CalendarMinMaxRange,
  },
  computed: {
    healthcheckDataCenters() {
      return this.$store.state.healthcheckLogs.healthcheckDataCenters
    },
    aggregationTypes() {
      return this.healthcheck.aggregateFunctionTypeOptions
    },
    loadingCount() {
      return this.$store.state.healthcheckLogs.fetchingHealthcheckLogsCount
    },
    healthcheckLogs() {
      return this.$store.state.healthcheckLogs.healthcheckLogsCharts
    },
    healthcheckStats() {
      return this.$store.state.healthcheckLogs.healthcheckStats
    },
    healthcheckLogsCount() {
      return this.$store.state.healthcheckLogs.healthcheckLogsCount
    },
    loading() {
      return this.$store.state.healthcheckLogs.fetchingHealthcheckStats
    },
    medianResTime() {
      return this.calculateMedian(this.getResponseTimeSeries())
    },
    averageResTime() {
      return this.calculateAverage(this.getResponseTimeSeries(), 2)
    },
    highestResTime() {
      return Math.max(...this.getResponseTimeSeries()).toFixed(2)
    },
    healthcheck() {
      return this.$store.state.healthchecks.healthcheck
    },
    responseStatusCodeRange() {
      if (this.healthcheck.expectedResponse >= 400) {
        return '4xx-5xx'
      }
      if (this.healthcheck.expectedResponse >= 300) {
        return '3xx'
      }
      if (this.healthcheck.expectedResponse >= 200) {
        return '2xx'
      }

      return '1xx'
    },
    selectedInterval: {
      get() {
        return this.$store.state.healthcheckLogs.selectedInterval
      },
      set(interval) {
        this.$store.commit('healthcheckLogs/SET_SELECTED_INTERVAL', interval)
      },
    },
    selectedDataCenter: {
      get() {
        return this.$store.state.healthcheckLogs.selectedDataCenter
      },
      set(dataCenter) {
        this.$store.commit('healthcheckLogs/SET_SELECTED_DATACENTER', dataCenter)
      },
    },
    selectedAggregation: {
      get() {
        return this.$store.state.healthcheckLogs.selectedAggregation
      },
      set(aggregation) {
        this.$store.commit('healthcheckLogs/SET_SELECTED_AGGREGATION', aggregation)
      },
    },
    storedDateRange: {
      get() {
        return this.$store.state.healthcheckLogs.dateRange
      },
      set(value) {
        this.$store.commit('healthcheckLogs/SET_DATE_RANGE', value)
      },
    },
  },
  created() {
    if (this.storedDateRange.healthcheckId !== this.healthcheck?.id) {
      this.storedDateRange = {
        ...this.storedDateRange,
        healthcheckId: this.healthcheck.id,
      }
      this.getLogsAndCheckDataCenters()
    } else if (this.healthcheckStats.length <= 0) {
      this.getLogsAndCheckDataCenters()
    }
  },
  methods: {
    getLogsAndCheckDataCenters() {
      if (this.healthcheckDataCenters.length <= 0) {
        this.clearStore()
        this.getHealthcheckDataCenters()
      } else {
        this.getHealthcheckStats()
      }
    },
    getHealthcheckDataCenters() {
      const dataCenters = [{ id: 0, name: 'All', slug: 'Data Centers' }]

      this.healthcheck.healthcheckLogStatuses.forEach(hl => {
        dataCenters.push(hl.dataCenter)
      })

      if (dataCenters.length > 0 && this.healthcheckDataCenters.length <= 0) {
        this.changeDataCenter(dataCenters[0])
        this.$store.commit('healthcheckLogs/SET_HEALTHCHECK_DATACENTERS', dataCenters)
      }

      return dataCenters
    },
    changeDataCenter(dataCenter) {
      if (dataCenter === this.selectedDataCenter) return
      this.selectedDataCenter = dataCenter
      this.getHealthcheckStats()
      this.clearResults()
    },
    changeAggregationType(aggregation) {
      if (aggregation === this.selectedAggregation) return
      this.selectedAggregation = aggregation
      this.getHealthcheckStats()
      this.clearResults()
    },
    getHealthcheckStats() {
      const query = {
        dataCenterId: this.selectedDataCenter.id,
        aggregateFunction: this.selectedAggregation.id,
        dateFrom: this.storedDateRange.dateFrom,
        dateTo: this.storedDateRange.dateTo,
      }
      this.$store
        .dispatch('healthcheckLogs/fetchHealthcheckStats', {
          healthcheckId: this.$route.params.healthcheckId,
          query,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasHealthcheckLogsPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthcheck stats!',
          )
        })
        .then(() => {
          this.getHealthcheckLogsCount()
        })
    },
    getHealthcheckLogsCount() {
      this.$store
        .dispatch('healthcheckLogs/fetchHealthcheckLogsCount', { healthcheckId: this.$route.params.healthcheckId })
        .catch(error => {
          this.errorToast(
            error.response?.data || 'Something went wrong while fetching healthcheck logs stats!',
          )
        })
    },
    getResponseTimeSeries() {
      return this.healthcheckStats.map(e => e.responseTime)
    },
    returnSchedule(healthcheck) {
      if (healthcheck.scheduleType === 0) {
        return 'Every 30 seconds'
      }
      if (healthcheck.scheduleType === 1) {
        return `Every ${healthcheck.interval} minute${
          healthcheck.interval !== 1 ? 's' : ''
        }`
      }
      if (healthcheck.scheduleType === 2) {
        return `Every ${healthcheck.interval} hour${
          healthcheck.interval !== 1 ? 's' : ''
        }`
      }
      return this.getText(healthcheck.scheduleOptions, healthcheck.scheduleType)
    },
    updateDateRange(date) {
      if (this.healthcheckDataCenters.length <= 0) {
        this.clearStore()
        this.getHealthcheckDataCenters()
      } else {
        this.getHealthcheckStats(date)
      }
    },
    clearStore() {
      this.$store.commit('healthcheckLogs/SET_HEALTHCHECK_DATACENTERS', [])
      this.selectedDataCenter = { id: 0, name: 'All - Data Centers', slug: '' }
      this.selectedAggregation = { id: 0, value: 'Response Time Average' }
    },
    clearResults() {
      this.$store.commit('healthcheckLogs/CLEAR_HEALTHCHECK_LOGS')
    },
  },
}
</script>

<style lang="scss" scoped>
.loading-wrapper {
  padding: 1rem !important;
  margin: 1rem !important;
}
.status-code-1xx {
  color: #419EF7;
}
.status-code-2xx {
  color: #36b580;
}
.status-code-3xx {
  color: #ffab00;
}
.status-code-4xx-5xx{
  color: #ff4560;
}
.main-details{
  padding: 0.8rem;
  border: 1px solid #e0e5eb;
  border-radius: 10px;

  .details-input{
    .details-input-label{
      font-size: 1.5rem;
      font-weight: 500;
    }

    ::v-deep{
      .field-label{
        font-weight: 500;
      }
    }
  }
  fieldset {
    &:not(:last-child) {
      border-right: 1px solid #e0e5eb;
    }
  }
}
.chart-wrapper{
  border: 1px solid #e0e5eb;
  border-radius: 10px;
}
</style>
